import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Col, Input, Modal, Row, Select, Tooltip } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { api } from 'services/api';

function CreateTemplate({ token, onTemplateList }) {
	const intl = useIntl();
	const [domain, setDomain] = useState('');
	const [open, setOpen] = useState(false);
	const [charCount, setCharCount] = useState(0);
	const [confirmLoading, setConfirmLoading] = useState(false);
	const [template, setTemplate] = useState({
		name: '',
		lang: '',
		category: '',
		type: '',
		message: '',
		variables: [],
	});
	const [variableCounter, setVariableCounter] = useState(1);

	useEffect(() => {
		window.BX24.init(async () => {
			const auth = window.BX24 ? window.BX24.getAuth() : { domain: 'solinka.bitrix24.com' };
			const domain = auth.domain;
			setDomain(domain);
		});
	}, []);

	const handleAddVariable = () => {
		const newVariable = '';
		setTemplate((prev) => ({
			...prev,
			message: `${prev.message} {{${variableCounter}}}`,
			variables: [...prev.variables, newVariable],
		}));
		setVariableCounter((prev) => prev + 1);
	};

	const handleVariableChange = (index, value) => {
		const updatedVariables = [...template.variables];
		updatedVariables[index] = value;
		setTemplate({ ...template, variables: updatedVariables });
	};

	const showModal = () => setOpen(true);

	const handleCreateTemplate = async () => {
		setConfirmLoading(true);

		const request = {
			name: template.name,
			language: template.lang,
			category: template.category,
			components: [
				{
					type: 'BODY',
					text: template.message,
					example: {
						body_text: [template.variables],
					},
				},
			],
			token,
			domain,
		};

		try {
			const result = await api.post('createTemplate', request);
		} catch (error) {
			console.log('error:', error);
		} finally {
			setOpen(false);
			setConfirmLoading(false);
			onTemplateList();
		}
	};

	const handleCancel = () => setOpen(false);

	const handleInputChange = (e) => {
		const newName = e.target.value;
		setTemplate({ ...template, name: newName });
		setCharCount(newName.length);
	};

	return (
		<>
			<button onClick={showModal} className="btn-new-template">
				<FormattedMessage id="new_template" />
			</button>
			<Modal
				title={intl.formatMessage({ id: 'template_create' })}
				visible={open}
				onOk={handleCreateTemplate}
				confirmLoading={confirmLoading}
				onCancel={handleCancel}
				okText={intl.formatMessage({ id: 'btn_template_create' })}
				cancelText={intl.formatMessage({ id: 'btn_template_cancel' })}
				width={800}
			>
				<Row>
					<span className="bold-text">
						<FormattedMessage id="template_form_name" />
					</span>
					<Input
						maxLength={450}
						addonAfter={`${charCount}/450`}
						placeholder={intl.formatMessage({ id: 'template_placeholder_name' })}
						onChange={handleInputChange}
						className="input-field"
					/>
				</Row>
				<Row style={{ marginTop: '10px' }}>
					<Col span={24}>
						<span className="bold-text">
							<FormattedMessage id="template_form_lang" />
						</span>
					</Col>
					<Col span={24}>
						<Select
							showSearch
							className="select-field"
							placeholder={intl.formatMessage({ id: 'template_placeholder_lang' })}
							onChange={(value) => setTemplate({ ...template, lang: value })}
						>
							<Select.Option value="pt_BR">Português (BR)</Select.Option>
						</Select>
					</Col>
				</Row>
				<Row style={{ marginTop: '10px' }}>
					<Col span={24}>
						<span className="bold-text">
							<FormattedMessage id="template_form_category" />
						</span>
					</Col>
					<Col span={24}>
						<Select
							className="select-field"
							placeholder={intl.formatMessage({ id: 'template_placeholder_category' })}
							onChange={(value) => setTemplate({ ...template, category: value })}
						>
							<Select.Option value="UTILITY">Utilidade</Select.Option>
							<Select.Option value="MARKETING">Marketing</Select.Option>
							<Select.Option value="AUTHENTICATION">Autenticação</Select.Option>
						</Select>
					</Col>
				</Row>
				<Row style={{ marginTop: '10px' }}>
					<Col span={24}>
						<span className="bold-text">
							<FormattedMessage id="template_form_type" />
						</span>
					</Col>
					<Col span={24}>
						<Select
							className="select-field"
							placeholder={intl.formatMessage({ id: 'template_placeholder_type' })}
							onChange={(value) => setTemplate({ ...template, type: value })}
						>
							<Select.Option value="text">Texto</Select.Option>
							<Select.Option value="seletor">Seletor de lista</Select.Option>
							<Select.Option value="media">Mídia</Select.Option>
						</Select>
					</Col>
				</Row>

				{template.type === 'text' && (
					<>
						<Row style={{ marginTop: '10px' }}>
							<Col span={24}>
								<span className="bold-text">
									<FormattedMessage id="template_option_text" />
								</span>
							</Col>
							<Col span={24}>
								<TextArea
									value={template.message}
									maxLength={1600}
									onChange={(e) => setTemplate({ ...template, message: e.target.value })}
									placeholder={intl.formatMessage({ id: 'template_text_placeholder' })}
									className="text-area"
								/>
							</Col>
							<Col span={24}>
								<span onClick={handleAddVariable} className="add-variable-text">
									<PlusOutlined /> <FormattedMessage id="template_add_variable" />
									<Tooltip
										placement="top"
										className="ml-2"
										title={<FormattedMessage id="template_tooltip" />}
									>
										<InfoCircleOutlined className="info-icon" />
									</Tooltip>
								</span>
							</Col>
						</Row>

						{template.variables.length > 0 && (
							<Row style={{ marginTop: '10px' }}>
								<Col span={24}>
									<span className="bold-text">
										<FormattedMessage id="template_example_body" />
									</span>
								</Col>
								<Col span={24}>
									{template.variables.map((variable, index) => (
										<Col span={10} key={index} style={{ marginTop: '5px', marginLeft: '5px' }}>
											<Input
												placeholder={intl.formatMessage(
													{ id: 'template_variable_placeholder' },
													{ number: index + 1 },
												)}
												value={variable}
												onChange={(e) => handleVariableChange(index, e.target.value)}
												className="input-field"
											/>
										</Col>
									))}
								</Col>
							</Row>
						)}
					</>
				)}
			</Modal>
		</>
	);
}

export default CreateTemplate;
